<template>
  <div v-if="editableMapLayers" id="layerHandler" :class="{ layerBtnCollapsed: isHidden }">
    <div class="titleBar" @click.stop="$emit('toggleHandler')">
      <div class="titleIcon">
        <span class="glyphicons glyphicons-sort backgroundIcon"></span><span class="glyphicons glyphicons-cadastral-map frontIcon"></span>
      </div>
      {{ $t("map.layer.title") }}
    </div>

    <div class="tabBar">
      <span
        v-for="tab in tabs"
        :key="tab.key"
        :class="['tab', { activeTab: tab.key === activeTabKey }]"
        @click.stop="selectTab(tab)"
      >{{ $t("map.layer.category." + tab.key) }}<span
        v-if="tabHasVisibleLayer(tab)"
        :class="['glyphicon glyphicon-eye-open', 'tabEyeIcon']"
      ></span></span>
    </div>

    <div class="layerList">
      <ul v-if="activeTabKey === 'ProjectData'">
        <li v-for="node in projectDataTree" :key="node.key" class="expanded">
          <div class="layerCategoryTitle">
            <input :id="'pd-' + node.key" type="checkbox" :checked="node.visible" @change="$emit('toggleProjectNode', node)" />

            <label :for="'pd-' + node.key" class="noselect">
              <span class="ckbx"></span>
              <span class="layerName" :title="node.label">{{ node.label }}</span>
            </label>

            <span
              v-if="node.children && node.children.length > 0"
              :class="[expandedNodes[node.key] ? 'glyphicon glyphicon-chevron-up' : 'glyphicon glyphicon-chevron-down', 'collapseIcon']"
              @click="toggleNode(node.key)"
            ></span>
            <template v-if="node.layer">
              <span class="glyphicons glyphicons-adjust opacityIcon"></span>

              <input
                v-model="node.layer.values_.opacityPercentage"
                class="opacityModifier"
                type="number"
                inputmode="numeric"
                min="0"
                max="100"
                step="10"
                v-on:wheel="scrollOpacity"
                @input="applyLayerConfiguration(node.layer)"
              />
              <div class="number-spinner">
                <div class="up noselect" @click="changeOpacity(node.layer, 5, 0, 100)">
                  <span class="glyphicons glyphicons-plus"></span>
                </div>
                <div class="down noselect" @click="changeOpacity(node.layer, -5, 0, 100)">
                  <span class="glyphicons glyphicons-minus"></span>
                </div>
              </div>
            </template>
          </div>

          <ul v-if="node.children">
            <li
              v-for="child in node.children"
              :key="child.key"
              :class="[expandedNodes[node.key] ? 'expanded' : 'collapsed']"
            >
              <div class="layerCategoryTitle">
                <input :id="'pd-' + child.key" type="checkbox" :checked="child.visible" @change="$emit('toggleProjectNode', child)" />

                <label :for="'pd-' + child.key" class="noselect">
                  <span class="ckbx"></span>
                  <span class="layerName" :title="child.label">{{ child.label }}</span>
                </label>

                <span
                  v-if="child.kind === 'kmlLayer'"
                  :class="[expandedNodes[child.key] ? 'glyphicon glyphicon-chevron-up' : 'glyphicon glyphicon-chevron-down', 'collapseIcon']"
                  @click="toggleNode(child.key)"
                ></span>

                    <template v-if="child.layer">
                  <span class="glyphicons glyphicons-adjust opacityIcon"></span>

                  <input
                    v-model="child.layer.values_.opacityPercentage"
                    class="opacityModifier"
                    type="number"
                    inputmode="numeric"
                    min="0"
                    max="100"
                    step="10"
                    v-on:wheel="scrollOpacity"
                    @input="applyLayerConfiguration(child.layer)"
                  />
                  <div class="number-spinner">
                    <div class="up noselect" @click="changeOpacity(child.layer, 5, 0, 100)">
                      <span class="glyphicons glyphicons-plus"></span>
                    </div>
                    <div class="down noselect" @click="changeOpacity(child.layer, -5, 0, 100)">
                      <span class="glyphicons glyphicons-minus"></span>
                    </div>
                  </div>
                </template>
              </div>

              <ul v-if="child.kind === 'kmlLayer'">
                <li
                  v-for="(featureGroup, key) in prepareMapLayers(child.layer)"
                  :key="key"
                  :class="[expandedNodes[child.key] ? 'expanded' : 'collapsed']"
                >
                  <input
                    :id="featureGroupId(child.layer, key)"
                    type="checkbox"
                    :checked="featureGroup['visible']"
                    @change="setFeaturesVisible(child.layer, featureGroup.featureIDS, $event.target.checked)"
                  />
                  <label :for="featureGroupId(child.layer, key)" class="noselect">
                    <span class="ckbx"></span>
                    <span class="layerName" :title="featureGroup['title']">{{ featureGroup["title"] }}</span>
                  </label>
                </li>
              </ul>
            </li>
          </ul>
        </li>
      </ul>

      <ul v-for="(value, groupKey) in visibleGroups" v-else :key="groupKey">
        <template v-if="showGroupTitles">
          <span class="listTitle" @click="collapseGroup(groupKey)">{{ $t("map.layer.category." + groupKey) }}</span>

          <span
            :class="[expandedGroups[groupKey].isGroupExpanded || false ? 'glyphicon glyphicon-chevron-up' : 'glyphicon glyphicon-chevron-down', 'collapseIcon']"
            @click="collapseGroup(groupKey)"
          ></span>

          <span v-if="getGroupVisibility(groupKey, value)" :class="['glyphicon glyphicon-eye-open', 'eyeIcon']"></span>
        </template>

        <li
          v-for="(mapLayer, layerKey) in value"
          :key="layerKey"
          :class="[!showGroupTitles || expandedGroups[groupKey].isGroupExpanded ? 'expanded' : 'collapsed']"
        >
          <div class="layerCategoryTitle">
            <input :id="layerId(mapLayer)" v-model="mapLayer.values_.visible" type="checkbox" @change="setLayerVisible(mapLayer)" />

            <label :for="layerId(mapLayer)" class="noselect">
              <span class="ckbx"></span>
              <span v-if="mapLayer.values_.name.includes('.')" class="layerName" :title="$t(mapLayer.values_.name.toString())">{{ $t(mapLayer.values_.name.toString()) }}</span>
              <span v-else class="layerName" :title="mapLayer.values_.name">{{ mapLayer.values_.name }}</span>
            </label>

            <span
              v-if="hasSubLayers(mapLayer) || mapLayer.type === 'kmlLayer'"
              :class="[
                expandedGroups[groupKey].expandedLayers[layerKey] ? 'glyphicon glyphicon-chevron-up' : 'glyphicon glyphicon-chevron-down',
                'collapseIcon'
              ]"
              @click="collapseLayers(groupKey, layerKey)"
            ></span>

            <span class="glyphicons glyphicons-adjust opacityIcon"></span>

            <input
              v-model="mapLayer.values_.opacityPercentage"
              class="opacityModifier"
              type="number"
              inputmode="numeric"
              min="0"
              max="100"
              step="10"
              v-on:wheel="scrollOpacity"
              @input="applyLayerConfiguration(mapLayer)"
            />
            <div class="number-spinner">
              <div class="up noselect" @click="changeOpacity(mapLayer, 5, 0, 100)">
                <span class="glyphicons glyphicons-plus"></span>
              </div>
              <div class="down noselect" @click="changeOpacity(mapLayer, -5, 0, 100)">
                <span class="glyphicons glyphicons-minus"></span>
              </div>
            </div>
          </div>

          <ul v-if="hasSubLayers(mapLayer)">
            <li
              v-for="(subMapLayer, key) in mapLayer.values_.subLayers"
              :key="key"
              :class="[expandedGroups[groupKey].expandedLayers[layerKey] ? 'expanded' : 'collapsed']"
            >
              <input :id="subLayerId(mapLayer, key)" v-model="subMapLayer.visible" type="checkbox" @change="setSubLayerVisible(mapLayer)" />
              <label :for="subLayerId(mapLayer, key)" class="noselect">
                <span class="ckbx"></span>
                <span class="layerName" :title="key">{{ subMapLayer["title"] }}</span>
              </label>
            </li>
          </ul>
          <ul v-if="mapLayer.type === 'kmlLayer'">
            <li
              v-for="(subMapLayer, key) in prepareMapLayers(mapLayer)"
              :key="key"
              :class="[expandedGroups[groupKey].expandedLayers[layerKey] ? 'expanded' : 'collapsed']"
            >
              <input :id="featureGroupId(mapLayer, key)" type="checkbox" :value="subMapLayer['visible']"  :checked="subMapLayer['visible']" @change="setFeaturesVisible(mapLayer, subMapLayer.featureIDS, $event.target.checked)" />
              <label :for="featureGroupId(mapLayer, key)" class="noselect">
                <span class="ckbx"></span>
                <span class="layerName" :title="key">{{ subMapLayer["title"] }}</span>
              </label>
            </li>
          </ul>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>

// One tab per country, so the list is never one long scroll. The tab is labelled with its first
// category, which the locales already name.
const TABS = [
  { key: 'ProjectData', categories: ['ProjectData'] },
  { key: 'Company', categories: ['Company'] },
  { key: 'Base', categories: ['Base'] },
  { key: 'NL', categories: ['NL', 'TijdreisNL'] },
  { key: 'BE', categories: ['BE', 'TijdreisVlaanderern'] },
  { key: 'FR', categories: ['FR', 'TijdreisFR'] },
  { key: 'ES', categories: ['ES', 'TijdreisES'] }
]

export default {
  name: 'layer-handler',

  props: {
    map: {
      type: Object,
      required: true
    },
    editableMapLayers: {
      type: Boolean,
      required: true
    },
    isHidden: {
      type: Boolean,
      required: true
    },
    groupedEditableMapLayers: {
      type: Object,
      required: true
    },
    expandedGroups: {
      type: Object,
      required: true
    },
    projectDataTree: {
      type: Array,
      required: true
    }
  },

  data () {
    return {
      activeTab: 'ProjectData',
      expandedNodes: {}
    }
  },

  computed: {
    /**
     * The tabs that hold something. A category the list above does not know about becomes a tab of
     * its own, so a new one can never go missing from the panel.
     */
    tabs () {
      const categories = Object.keys(this.groupedEditableMapLayers)
      const known = TABS.reduce((all, tab) => all.concat(tab.categories), [])

      const filled = TABS.filter((tab) => tab.categories.some((category) => categories.includes(category)))
      const unknown = categories
        .filter((category) => !known.includes(category))
        .map((category) => ({ key: category, categories: [category] }))

      return filled.concat(unknown)
    },

    // Falls back to the first tab, so an empty project still opens on something.
    activeTabKey () {
      const tab = this.tabs.find((candidate) => candidate.key === this.activeTab)

      if (tab) {
        return tab.key
      }

      return this.tabs.length > 0 ? this.tabs[0].key : ''
    },

    visibleGroups () {
      const tab = this.tabs.find((candidate) => candidate.key === this.activeTabKey)

      if (!tab) {
        return {}
      }

      return tab.categories.reduce((groups, category) => {
        if (this.groupedEditableMapLayers[category]) {
          groups[category] = this.groupedEditableMapLayers[category]
        }

        return groups
      }, {})
    },

    // A tab holding a single category already says its name in the tab itself.
    showGroupTitles () {
      return Object.keys(this.visibleGroups).length > 1
    }
  },

  methods: {
    // Read from the layer rather than from its WMS params: the sublayers have to be there before
    // the layer is drawn, and a layer that has not been asked for yet has no source at all.
    hasSubLayers (mapLayer) {
      const subLayers = mapLayer.values_.subLayers

      return !!subLayers && Object.keys(subLayers).length > 0
    },
    prepareMapLayers(mapLayer) {
      let klicLayer = {}
      const subNameKey = 'TI:SublayerName'
      mapLayer.getSource().getFeatures().forEach(feature => {
        if (!feature.values_.hasOwnProperty(subNameKey)) return
        const groupName = feature.values_[subNameKey].value
        if (!klicLayer[groupName]) klicLayer[groupName] = []

        klicLayer[groupName] = [...klicLayer[groupName], feature.ol_uid]
      })

      const klicLayerArray = []

      Object.keys(klicLayer).forEach((key) => {
        klicLayerArray.push({
          type: 'features',
          featureIDS: klicLayer[key],
          visible: true,
          title: key
        })
      })

      return klicLayerArray
    },
    toggleNode (key) {
      this.$set(this.expandedNodes, key, !this.expandedNodes[key])
    },
    selectTab (tab) {
      this.activeTab = tab.key

      // Open the country group with its tab. The time travel beside it, which runs to hundreds of
      // years, stays folded.
      if (tab.categories.length > 1) {
        this.$emit('expandGroup', tab.categories[0])
      }
    },
    // An eye on the tab as well, so a country that has something switched on says so without being
    // opened.
    tabHasVisibleLayer (tab) {
      return tab.categories.some((category) => {
        const layers = this.groupedEditableMapLayers[category]

        return !!layers && layers.some((layer) => layer.getVisible())
      })
    },
    getGroupVisibility (key, value) {
      for (let layer of value) {
        if (layer.getVisible()) return true
      }
      return false
    },
    collapseGroup (groupKey) {
      this.$emit('clickGroupArrow', groupKey)
    },
    collapseLayers (groupKey, key) {
      this.$emit('clickLayerArrow', groupKey, key)
    },
    setLayerVisible (mapLayer) {
      this.$emit('clickLayerBox', mapLayer)
    },
    scrollOpacity (event) {
      this.$emit('scrollOpacity', event)
    },
    applyLayerConfiguration (mapLayer) {
      this.$emit('clickOpacity', mapLayer)
    },
    changeOpacity (mapLayer, increment, min, max) {
      this.$emit('clickSpinner', mapLayer, increment, min, max)
    },
    setSubLayerVisible (mapLayer) {
      this.$emit('clickSubLayerBox', mapLayer)
    },
    setFeaturesVisible (mapLayer, featureIDs, isVisible) {
      this.$emit('clickKMLVisible', { mapLayer, featureIDs, isVisible })
    },
    // Each kind of checkbox gets its own prefix and separators: gluing an index onto a layer uid
    // ("1" + "639") could produce the id of another layer's checkbox ("16" + "39").
    layerId (mapLayer) {
      return `layer-${mapLayer.ol_uid}`
    },
    subLayerId (mapLayer, subLayerKey) {
      return `sublayer-${mapLayer.ol_uid}-${subLayerKey}`
    },
    featureGroupId (mapLayer, groupIndex) {
      return `featuregroup-${mapLayer.ol_uid}-${groupIndex}`
    }
  }
}
</script>

<style lang="less">
.noselect {
  -webkit-touch-callout: none; /* iOS Safari */
  -webkit-user-select: none; /* Safari */
  -khtml-user-select: none; /* Konqueror HTML */
  -moz-user-select: none; /* Firefox */
  -ms-user-select: none; /* Internet Explorer/Edge */
  user-select: none;
  /* Non-prefixed version, currently
                                     supported by Chrome and Opera */
}

.layerBtnCollapsed {
  height: 38px !important;
  width: 38px !important;
  overflow: hidden;
  cursor: pointer;
  transition: 0.3s;
}

.layerBtnCollapsed:hover {
  transform: scale(0.9);
}

#layerHandler {
  position: absolute;
  z-index: 1;
  bottom: 10px;
  left: 60px;
  background-color: #eee;
  transition: 0.3s;
  width: 700px;
  // However narrow the map gets, keep the panel inside it with the same 10px it has at the bottom.
  max-width: calc(100% - 70px);
  height: 50%;
  display: flex;
  flex-direction: column;

  .titleBar {
    background-color: @primaryGreen;
    padding: 10px;
    color: @white;
    position: relative;
    padding-left: 40px;
    cursor: pointer;
    flex: 0 0 auto;

    .backgroundIcon {
      position: absolute;
      left: 0px;
      top: 0px;
      line-height: 28px;
      font-size: 20px;
    }

    .frontIcon {
      position: absolute;
      left: 0px;
      top: 11px;
      color: @primaryGreen;
      font-size: 13px;
    }
  }
  .tabBar {
    background-color: @primaryGreen;
    padding: 0px 10px 0px 10px;
    display: flex;
    flex-wrap: wrap;
    flex: 0 0 auto;

    .tab {
      padding: 3px 10px;
      margin-right: 4px;
      color: @white;
      cursor: pointer;
      white-space: nowrap;
      border-radius: 3px 3px 0px 0px;

      &:hover {
        background-color: rgba(255, 255, 255, 0.25);
      }

      &.activeTab {
        background-color: #eee;
        color: #333;
        font-weight: bold;
      }

      // The global .glyphicon padding would make the whole tab bar taller.
      .tabEyeIcon {
        padding: 0px;
        margin-left: 5px;
        font-size: 11px;
      }
    }
  }

  .layerList {
    padding: 10px;
    padding-right: 0px;
    flex: 1 1 auto;
    overflow-y: auto;

    ul {
      list-style: none;
      margin: 0;
      padding: 0;
      .listTitle {
        font-weight: bold;
        display: inline-block;
      }
      .listTitle:not(:first-child) {
        margin-top: 10px;
      }
      .eyeIcon {
        float: right;
      }
      .collapseIcon {
        cursor: pointer;
      }
      ul {
        margin-left: 10px;
      }
      li {
        .layerCategoryTitle {
          display: flex;
        }
      }
      li.collapsed {
        display: none;
      }
      li.expanded {
        display: block;
      }
      li:hover {
        .number-spinner {
          display: inline-block !important;
        }
      }
    }

    .opacityIcon {
      width: 27px;
      font-size: 15px;
      margin-right: -5px;
    }
    .opacityModifier {
      background-color: transparent;
      border: none;
      width: 35px;
      vertical-align: top;
      font-size: 15px;
      line-height: 25px;
      padding-left: 3px;

      &:focus {
        outline-color: @primaryGreen;
        outline-style: solid;
        outline-width: 1px;
      }
    }

    input[type="number"]::-webkit-outer-spin-button,
    input[type="number"]::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }

    input[type="number"] {
      -moz-appearance: textfield;
    }

    .number-spinner {
      display: none;
      height: 25px;
      overflow: hidden;

      .up {
        float: left;
        font-weight: bold;
        cursor: pointer;
        margin-left: -5px;
        margin-top: -2px;
      }

      .down {
        float: left;
        font-weight: bold;
        cursor: pointer;
        margin-right: -5px;
        margin-top: -2px;
      }
    }

    input[type="checkbox"] {
      display: none;
      & + label {
        position: relative;
        font-weight: normal;
        cursor: pointer;
        margin-bottom: 0px;
        height: 27px;
        .ckbx {
          display: inline-block;
          width: 12px;
          height: 12px;
          margin-right: 5px;
          border: 1px solid #9d9d9d;
          background-color: @white;
          cursor: pointer;
          margin-bottom: 7px;
        }
        .layerName {
          display: inline-block;
          overflow: hidden;
          line-height: 27px;
        }
      }
      &:checked + label .ckbx {
        background-color: @primaryGreen;
      }
    }
  }
}

.collapsed {
  display: none;
}
.expanded {
  display: block;
}
</style>
